import { useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowLeft, Plus } from 'lucide-react';
import { Badge, Button, ConfirmDialog, ErrorPanel, PageHeader, PanelCard, Spinner } from '../../school_panel/components/ui';
import {
  useAuthoringChapters, useAuthoringExam, useDeleteExamAttachment, useUploadExamAttachments, useDeleteAuthoringQuestion, usePublishAuthoringExam, useSaveAuthoringQuestion,
} from '../../school_panel/api/hooks';
import { SchoolApi } from '../../school_panel/api/client';
import AttachmentsField from '../../school_panel/components/AttachmentsField';
import type {
  AnnotateResult, Attachment, AuthoringQuestion, AuthoringQuestionType, ImportedQuestion, PublishProblem, QuestionPayload,
} from '../../school_panel/api/types';
import QuestionTypePicker, { TYPE_LABEL } from './components/QuestionTypePicker';
import QuestionList from './components/QuestionList';
import McqForm from './components/McqForm';
import FillBlankForm from './components/FillBlankForm';
import MatchingForm from './components/MatchingForm';
import WrittenForm from './components/WrittenForm';
import KindBadge from './components/KindBadge';
import ImportPanel from './components/ImportPanel';
import ImportReview from './components/ImportReview';
import AnnotateDialog from './components/AnnotateDialog';
import type { FieldErrors } from './components/shared';
import { gradeLabel } from '../../school_panel/lib/gradeLabel';

/** What the editor pane shows: a question form (existing, new, or one the AI imported). */
type Editing = { type: AuthoringQuestionType; question?: AuthoringQuestion; importIndex?: number };

function flatten(v: unknown): string {
  if (typeof v === 'string') return v;
  if (Array.isArray(v)) return v.map(flatten).filter(Boolean)[0] ?? '';
  if (v && typeof v === 'object') return Object.values(v as Record<string, unknown>).map(flatten).filter(Boolean)[0] ?? '';
  return '';
}

/** A 400 body keyed by field becomes one message per field. */
function fieldErrors(err: unknown): FieldErrors {
  const data = (err as { response?: { data?: unknown } })?.response?.data;
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    const out: FieldErrors = {};
    for (const [k, v] of Object.entries(data as Record<string, unknown>)) {
      const msg = flatten(v);
      if (msg) out[k] = msg;
    }
    if (Object.keys(out).length) return out;
  }
  return { detail: 'The question could not be saved. Try again.' };
}

function serverDetail(err: unknown): string | null {
  const d = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  return typeof d === 'string' && d ? d : null;
}

// An imported question as the form's `initial` (no id: saving creates it).
const asInitial = (it: ImportedQuestion): AuthoringQuestion => ({
  id: 0, explanation: it.question.explanation ?? '', options: [], answer: null,
  ...(it.question as unknown as Partial<AuthoringQuestion>),
  type: it.question.type,
  chapter: it.chapter ? { id: it.chapter.id, name: it.chapter.name } : null,
  reference: it.question.reference ?? '',
} as AuthoringQuestion);

// The AI's chapter, explanation and reference on an imported question; a blank result keeps what is there.
const withAnnotation = (it: ImportedQuestion, r: AnnotateResult): ImportedQuestion => ({
  ...it,
  chapter: r.chapter ?? it.chapter ?? null,
  question: {
    ...it.question,
    ...(r.chapter ? { chapter_id: r.chapter.id } : {}),
    ...(r.explanation ? { explanation: r.explanation } : {}),
    ...(r.reference ? { reference: r.reference } : {}),
  },
});

const FORM_KEYS = new Set(['text', 'options', 'answer', 'blanks', 'left', 'right', 'pairs', 'max_marks', 'model_answer', 'notes', 'explanation', 'chapter_id', 'reference']);

export default function WorksheetEditorPage() {
  const examId = Number(useParams().examId);
  const { data: exam, status, error } = useAuthoringExam(examId);
  const save = useSaveAuthoringQuestion(examId);
  const del = useDeleteAuthoringQuestion(examId);
  const publish = usePublishAuthoringExam(examId);
  const upload = useUploadExamAttachments(examId);
  const delAtt = useDeleteExamAttachment(examId);
  const [attToRemove, setAttToRemove] = useState<Attachment | null>(null);
  const [editing, setEditing] = useState<Editing | null>(null);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [toDelete, setToDelete] = useState<AuthoringQuestion | null>(null);
  const [problems, setProblems] = useState<PublishProblem[]>([]);
  const [publishError, setPublishError] = useState<string | null>(null);
  // AI import: the questions waiting for review, and why some could not be added.
  const [imported, setImported] = useState<ImportedQuestion[] | null>(null);
  const [importVersion, setImportVersion] = useState(0);
  const [importFailures, setImportFailures] = useState<Record<number, string>>({});
  const [adding, setAdding] = useState(false);
  const [annotating, setAnnotating] = useState(false);
  const chapters = useAuthoringChapters(examId);

  const startEditing = (e: Editing | null) => { setErrors({}); setEditing(e); };

  const dropImported = (indexes: number[]) => {
    setImported((cur) => {
      const next = (cur ?? []).filter((_, i) => !indexes.includes(i));
      return next.length ? next : null;
    });
    setImportFailures({});
    setImportVersion((v) => v + 1);
  };

  const onSave = async (payload: QuestionPayload) => {
    setErrors({});
    try {
      await save.mutateAsync({ qid: editing?.question?.id || undefined, body: payload });
      if (editing?.importIndex != null) dropImported([editing.importIndex]);
      setEditing(null);
      setProblems([]);
    } catch (err) {
      setErrors(fieldErrors(err));
    }
  };

  // Adds the ticked imported questions one by one (the same create call as the editor);
  // what fails stays in the review list with the server's reason.
  const addImported = async (indexes: number[]) => {
    if (!imported) return;
    setAdding(true);
    const done: number[] = [];
    const failed: Record<number, string> = {};
    for (const i of indexes) {
      try {
        await save.mutateAsync({ body: imported[i].question as QuestionPayload });
        done.push(i);
      } catch (err) {
        failed[i] = Object.values(fieldErrors(err))[0] ?? 'Could not be added.';
      }
    }
    setAdding(false);
    const left = imported.map((it, i) => ({ it, i })).filter(({ i }) => !done.includes(i));
    setImported(left.length ? left.map(({ it }) => it) : null);
    setImportFailures(Object.fromEntries(left.flatMap(({ i }, j) => (failed[i] ? [[j, failed[i]]] : []))));
    setImportVersion((v) => v + 1);
    if (done.length) toast.success(`${done.length} ${done.length === 1 ? 'question' : 'questions'} added`);
  };

  const onPublish = async () => {
    setProblems([]); setPublishError(null);
    try {
      await publish.mutateAsync();
    } catch (err) {
      const data = (err as { response?: { data?: { problems?: PublishProblem[]; detail?: string } } })?.response?.data;
      if (data?.problems?.length) setProblems(data.problems);
      else setPublishError(data?.detail || 'The worksheet could not be published. Try again.');
    }
  };

  if (status === 'pending') return <div className="flex justify-center p-10"><Spinner /></div>;
  if (status === 'error' || !exam) {
    return <ErrorPanel title="This worksheet could not be loaded" body={(error as Error | null)?.message || 'Try refreshing the page.'} />;
  }
  // Another teacher's worksheet: the read-only page (owner rule) instead of an editor that would 403.
  if (exam.can_edit === false) return <Navigate to={`/exams/exam-detail/${examId}`} replace />;

  const questions = exam.questions ?? [];
  const label = exam.kind === 'homework' ? 'homework' : 'worksheet';
  const Label = exam.kind === 'homework' ? 'Homework' : 'Worksheet';
  const formProps = {
    initial: editing?.question, saving: save.isPending, errors,
    onSave, onCancel: () => startEditing(null), chapters: chapters.data,
  };
  const generic = Object.entries(errors).filter(([k]) => !FORM_KEYS.has(k)).map(([, v]) => v);
  // Existing submissions keep their stored credit, but the review is recomputed
  // from the current answers, and deleting a question cascades to attempts.
  const published = exam.status === 'published';
  const publishedWarning = `This ${label} is published. Editing answers changes how existing submissions are reviewed; `
    + "deleting a question removes students' answers to it.";
  const problemIds = new Set(problems.map((p) => p.question_id).filter((x): x is number => x != null));
  const counts = questions.reduce<Record<string, number>>((m, q) => ({ ...m, [q.type]: (m[q.type] ?? 0) + 1 }), {});
  const meta = [exam.grade ? gradeLabel(exam.grade.name) : null, exam.subject?.name,
    `${questions.length} ${questions.length === 1 ? 'question' : 'questions'}`].filter(Boolean).join(' · ');

  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader
        eyebrow={`Academics · ${exam.kind === 'homework' ? 'Homework' : 'Worksheets'}`}
        title={exam.name}
        subtitle={<span className="inline-flex flex-wrap items-center gap-2"><KindBadge kind={exam.kind} />
          <Badge tone={published ? 'success' : 'warning'}>{published ? 'Published' : 'Draft'}</Badge>
          <span className="text-muted">{meta}</span></span>}
        trail={exam.kind === 'homework' ? [{ label: 'Homework', to: '/homework' }]
          : exam.kind === 'worksheet' ? [{ label: 'Worksheets', to: '/worksheets' }] : [{ label: 'Exams', to: '/exams/exam-list' }]}
        actions={
          <>
            <Link to={`/exams/exam-detail/${exam.id}`} className="inline-flex min-h-[40px] items-center rounded-[12px] border border-line-strong px-3.5 text-sm font-semibold text-ink no-underline hover:border-slate hover:text-ink">
              {Label} details
            </Link>
            {!published && <Button onClick={onPublish} loading={publish.isPending}>Publish</Button>}
          </>
        }
      />

      {published && (
        <p role="note" className="mb-[18px] rounded-[14px] border border-warning/40 bg-warning-bg px-4 py-3 text-sm text-warning-ink">
          {publishedWarning}
        </p>
      )}
      {publishError && <p role="alert" className="mb-[18px] rounded-[14px] bg-danger-bg px-4 py-3 text-sm text-danger-ink">{publishError}</p>}
      {problems.length > 0 && (
        <div className="mb-[18px] rounded-[16px] border border-danger/30 bg-danger-bg p-4">
          <p className="mb-2 text-sm font-semibold text-danger-ink">Fix these before publishing the {label}:</p>
          <ul aria-label="Publish problems" className="space-y-1 text-sm text-ink">
            {problems.map((p, i) => {
              const idx = questions.findIndex((q) => q.id === p.question_id);
              return (
                <li key={`${p.code}-${p.question_id ?? i}`} className="flex flex-wrap items-center gap-2">
                  <span>{p.message}</span>
                  {idx >= 0 && (
                    <button type="button" className="font-semibold text-primary underline" onClick={() => {
                      const q = questions[idx];
                      startEditing({ type: q.type as AuthoringQuestionType, question: q });
                    }}>Fix Q{idx + 1}</button>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <div className="grid grid-cols-1 items-start gap-[18px] lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
        <div className="space-y-[18px]">
          <PanelCard
            title="Questions"
            subtitle={questions.length
              ? Object.entries(counts).map(([t, n]) => `${n} ${TYPE_LABEL[t as AuthoringQuestionType]?.toLowerCase() ?? t}`).join(' · ')
              : 'None yet'}
            actions={editing || imported ? (
              <Button size="sm" variant="secondary" icon={<Plus size={14} aria-hidden />} onClick={() => { startEditing(null); }}>Add</Button>
            ) : undefined}>
            <QuestionList
              questions={questions}
              activeId={editing?.question?.id || null}
              problemIds={problemIds}
              onEdit={(q) => startEditing({ type: q.type as AuthoringQuestionType, question: q })}
              onDelete={setToDelete}
            />
          </PanelCard>
          <ImportPanel examId={examId} onExtracted={(qs) => { setImported(qs); setImportFailures({}); setImportVersion((v) => v + 1); startEditing(null); setAnnotating(true); }} />
        </div>

        <div className="space-y-[18px]">
          {editing ? (
            <section aria-label="Question editor" className="rounded-[20px] border border-line bg-surface p-[22px] shadow-card">
              <div className="mb-4 flex items-center justify-between gap-3">
                <h3 className="font-display text-[17px] font-bold text-ink">
                  {editing.importIndex != null ? 'Imported question' : editing.question ? `Edit Q${questions.findIndex((q) => q.id === editing.question?.id) + 1}` : 'New question'} · {TYPE_LABEL[editing.type] ?? editing.type}
                </h3>
                {editing.importIndex != null && (
                  <Button variant="ghost" size="sm" icon={<ArrowLeft size={14} aria-hidden />} onClick={() => startEditing(null)}>Back to imported</Button>
                )}
              </div>
              {generic.map((m) => <p key={m} role="alert" className="mb-3 rounded-[12px] bg-danger-bg px-3 py-2 text-sm text-danger-ink">{m}</p>)}
              {editing.type === 'multiple_choice' && <McqForm key={editing.question?.id || `new-${editing.importIndex}`} {...formProps} />}
              {editing.type === 'fill_blank' && <FillBlankForm key={editing.question?.id || `new-${editing.importIndex}`} {...formProps} />}
              {editing.type === 'matching' && <MatchingForm key={editing.question?.id || `new-${editing.importIndex}`} {...formProps} />}
              {editing.type === 'written' && <WrittenForm key={editing.question?.id || `new-${editing.importIndex}`} {...formProps} />}
            </section>
          ) : imported ? (
            <PanelCard title="Check the imported questions">
              <ImportReview key={importVersion} items={imported} adding={adding} failures={importFailures}
                onAdd={addImported}
                onAnnotate={() => setAnnotating(true)}
                onEdit={(i) => startEditing({ type: imported[i].question.type as AuthoringQuestionType, question: asInitial(imported[i]), importIndex: i })}
                onDiscard={() => { setImported(null); setImportFailures({}); }} />
            </PanelCard>
          ) : (
            <PanelCard title="Add a question" subtitle="Pick a type to write a question, or import a whole sheet with AI.">
              <QuestionTypePicker onPick={(t) => startEditing({ type: t })} />
            </PanelCard>
          )}

          {(exam.kind === 'worksheet' || exam.kind === 'homework') && (
            <PanelCard title="Attachments" subtitle="Files students open with the questions, such as a reading passage or a diagram.">
              <AttachmentsField
                attachments={exam.attachments ?? []}
                busy={upload.isPending}
                onOpen={(a) => { SchoolApi.downloadAttachment(a).catch(() => toast.error("Couldn't open the file. Try again.")); }}
                onAdd={(files) => upload.mutate(files, {
                  onSuccess: () => toast.success('Files added'),
                  onError: (e) => toast.error(serverDetail(e) ?? "The files couldn't be uploaded. Try again."),
                })}
                onRemove={(item) => { if (!(item instanceof File)) setAttToRemove(item); }}
              />
            </PanelCard>
          )}
        </div>
      </div>

      {imported && (
        <AnnotateDialog examId={examId} items={imported} open={annotating}
          onResults={(start, results) => setImported((cur) => cur && cur.map((it, i) => (
            i >= start && i < start + results.length ? withAnnotation(it, results[i - start]) : it)))}
          onClose={() => { setAnnotating(false); setImportVersion((v) => v + 1); }} />
      )}

      <ConfirmDialog
        isOpen={!!attToRemove}
        title={`Remove ${attToRemove?.name ?? ''}?`}
        confirmLabel="Remove"
        busy={delAtt.isPending}
        onCancel={() => setAttToRemove(null)}
        onConfirm={() => {
          if (!attToRemove) return;
          delAtt.mutate(attToRemove.id, {
            onSuccess: () => toast.success('File removed'),
            onError: (e) => toast.error(serverDetail(e) ?? "Couldn't remove the file. Try again."),
            onSettled: () => setAttToRemove(null),
          });
        }}
      />

      <ConfirmDialog
        isOpen={!!toDelete}
        title="Delete this question?"
        body={published ? `${publishedWarning} This cannot be undone.` : 'This cannot be undone.'}
        confirmLabel="Delete"
        busy={del.isPending}
        onCancel={() => setToDelete(null)}
        onConfirm={async () => {
          if (!toDelete) return;
          try { await del.mutateAsync(toDelete.id); } catch { /* the list refetch shows the real state */ } finally { setToDelete(null); }
        }}
      />
    </div>
  );
}
